import { createContext, useContext, useState, useEffect } from "react";

const LanguageContext = createContext();

// Detect browser language and map to supported languages
const detectBrowserLanguage = () => {
  const browserLang = navigator.language || navigator.userLanguage;
  const langCode = browserLang.split('-')[0].toLowerCase();
  
  // Map to supported languages
  if (langCode === 'pt') return 'pt';
  if (langCode === 'es') return 'es';
  return 'en'; // Default to English
};

const translations = {
  en: {
    // Language selector
    selectLanguage: "Select Your Language",
    continue: "Continue",
    
    // Lead capture
    getStartedFree: "Get Started Free",
    createAccount: "Create your account to access AI-powered peptide recommendations",
    continueWithGoogle: "Continue with Google",
    orUseEmail: "or use email",
    fullName: "Full Name",
    emailAddress: "Email Address",
    phoneNumber: "Phone Number",
    getAccess: "Get Access",
    secure: "Secure",
    freeToUse: "Free to use",
    verifyEmail: "Verify Your Email",
    enterCode: "Enter the 6-digit code sent to",
    verificationCode: "Verification Code",
    demoMode: "Demo Mode - Your code is:",
    verifyAndContinue: "Verify & Continue",
    resendCode: "Resend verification code",
    backToSignUp: "← Back to sign up",
    
    // Landing page
    aiPoweredPlanning: "AI-Powered Peptide Planning",
    designYourPerfect: "Design Your Perfect",
    peptideCycle: "Peptide Cycle",
    heroDescription: "Enter your goal and let AI create a personalized peptide protocol with precise dosages, timing, cost estimates, and a weekly schedule.",
    selectGoals: "Select your goals (choose one or more)",
    additionalGoal: "Additional goal (optional)",
    additionalGoalPlaceholder: "e.g., Improve skin quality, enhance cognitive function...",
    bodyWeight: "Body Weight (kg)",
    experienceLevel: "Experience Level",
    beginner: "Beginner",
    intermediate: "Intermediate",
    advanced: "Advanced",
    generatingCycle: "Generating Your Cycle...",
    generateMyCycle: "Generate My Peptide Cycle",
    
    // Goals
    fatLoss: "Fat Loss / Weight Loss",
    muscleBuilding: "Muscle Building / Bulking",
    fatBurnLeanMuscle: "Fat Burn + Increase Lean Muscle",
    enduranceRecovery: "Improve Endurance + Recovery",
    antiAging: "Anti-Aging / Longevity",
    recovery: "Recovery / Healing",
    sleep: "Sleep Optimization",
    healing: "Injury Healing",
    popular: "Popular",
    
    // Features section
    everythingYouNeed: "Everything You Need to Plan Your Cycle",
    featuresDescription: "Our AI analyzes your goals and provides comprehensive peptide protocols with all the details you need.",
    aiPoweredAnalysis: "AI-Powered Analysis",
    aiAnalysisDesc: "GPT-powered recommendations tailored to your specific goals and experience level",
    preciseDosing: "Precise Dosing",
    preciseDosingDesc: "Exact dosages in mg and IU with timing protocols for optimal results",
    cycleCalendar: "Cycle Calendar",
    cycleCalendarDesc: "Visual weekly schedule with exportable calendar view",
    costCalculator: "Cost Calculator",
    costCalculatorDesc: "Know exactly how many vials you need and total investment required",
    
    // How it works
    howItWorks: "How It Works",
    fourSimpleSteps: "Four simple steps to your personalized peptide protocol",
    step1Title: "Select Your Goals",
    step1Desc: "Choose one or multiple objectives",
    step2Title: "Get AI Analysis",
    step2Desc: "Receive personalized recommendations",
    step3Title: "View Your Cycle",
    step3Desc: "Complete protocol with schedule",
    step4Title: "Start Your Journey",
    step4Desc: "Purchase and begin",
    
    // Trust section
    researchBased: "Research-Based Protocols",
    researchBasedDesc: "Our AI recommendations are based on published research and established peptide protocols. Always consult with a healthcare professional before starting any peptide regimen.",
    evidenceBased: "Evidence-Based",
    goalSpecific: "Goal-Specific",
    exportablePlans: "Exportable Plans",
    
    // Results page
    yourPersonalized: "Your Personalized",
    cycleProtocol: "Cycle Protocol",
    goal: "Goal",
    duration: "Duration",
    weeks: "weeks",
    totalCost: "Total Cost",
    peptideProtocol: "Peptide Protocol",
    perDay: "per day",
    timing: "Timing",
    vials: "vials needed",
    weeklySchedule: "Weekly Schedule",
    week: "Week",
    beginnerGuide: "Beginner's Guide",
    mixingInstructions: "Mixing Instructions",
    storageGuidelines: "Storage Guidelines",
    syringeRecommendations: "Syringe Recommendations",
    purchaseLinks: "Where to Purchase",
    discountMessage: "Use these links for exclusive discounts on quality peptides",
    saveCycle: "Save This Cycle",
    cycleSaved: "Cycle saved to your dashboard!",
    exportPDF: "Export PDF",
    generateNew: "Generate New Cycle",
    
    // Results page additional
    aiRecommendation: "AI Recommendation Summary",
    suppliesNeeded: "Supplies You'll Need",
    mixingGuide: "Step-by-Step Mixing Guide (Reconstitution)",
    mixingGuideDesc: "Follow these steps carefully when mixing your peptides with bacteriostatic water",
    injectionTips: "Injection Tips",
    important: "Important!",
    neverShake: "NEVER shake or swirl the vial vigorously - this can damaged the peptide molecules. Always let the water drip down the side of the vial and gently roll between your palms.",
    cycleStart: "Cycle Start",
    cycleEnd: "Cycle End",
    scheduleFor: "Schedule for",
    noInjections: "No injections scheduled for this day",
    weeklyOverview: "Weekly Protocol Overview",
    restDay: "Rest day",
    exclusiveDiscount: "Exclusive Discount Available!",
    useAffiliateLinks: "Use our affiliate links below to get special discounts on your peptide orders. These trusted vendors offer quality research peptides at competitive prices.",
    shopAt: "Shop at",
    getDiscount: "Get Discount",
    shoppingList: "Shopping List",
    pricePerVial: "Price/Vial",
    subtotal: "Subtotal",
    totalEstimatedCost: "Total Estimated Cost",
    pricesEstimate: "Prices are estimates. Use our affiliate links above to get current pricing and discounts.",
    tipsForSuccess: "Tips for Success",
    importantWarnings: "Important Warnings",
    disclaimer: "Disclaimer",
    disclaimerText: "This information is for research and educational purposes only. Peptides are sold strictly for research purposes. Always consult with a qualified healthcare professional before starting any new supplement or research compound. Individual results may vary.",
    benefits: "Benefits",
    sideEffects: "Potential Side Effects",
    
    // Dashboard
    dashboard: "Dashboard",
    welcomeBack: "Welcome back",
    savedCycles: "Saved Cycles",
    injectionReminders: "Injection Reminders",
    affiliateAnalytics: "Affiliate Analytics",
    noCycles: "No saved cycles yet",
    noReminders: "No reminders set",
    addReminder: "Add Reminder",
    peptideName: "Peptide Name",
    dosage: "Dosage",
    time: "Time",
    days: "Days",
    save: "Save",
    cancel: "Cancel",
    edit: "Edit",
    delete: "Delete",
    totalClicks: "Total Clicks",
    clicksByVendor: "Clicks by Vendor",
    
    // Navigation
    logout: "Logout",
    loggedOut: "Logged out",
    
    // Errors and messages
    pleaseSelectGoal: "Please select at least one goal",
    cycleGenerated: "Cycle generated successfully!",
    failedToGenerate: "Failed to generate cycle. Please try again.",
    verificationSent: "Verification code sent! Check your email.",
    emailVerified: "Email verified! Welcome to PeptidePlanner AI",
    invalidCode: "Invalid verification code",
    newCodeSent: "New verification code sent!",
    
    // Footer
    forResearch: "For research and educational purposes only.",
  },
  
  pt: {
    // Language selector
    selectLanguage: "Selecione Seu Idioma",
    continue: "Continuar",
    
    // Lead capture
    getStartedFree: "Comece Gratuitamente",
    createAccount: "Crie sua conta para acessar recomendações de peptídeos com IA",
    continueWithGoogle: "Continuar com Google",
    orUseEmail: "ou use email",
    fullName: "Nome Completo",
    emailAddress: "Endereço de Email",
    phoneNumber: "Número de Telefone",
    getAccess: "Obter Acesso",
    secure: "Seguro",
    freeToUse: "Grátis para usar",
    verifyEmail: "Verifique Seu Email",
    enterCode: "Digite o código de 6 dígitos enviado para",
    verificationCode: "Código de Verificação",
    demoMode: "Modo Demo - Seu código é:",
    verifyAndContinue: "Verificar e Continuar",
    resendCode: "Reenviar código de verificação",
    backToSignUp: "← Voltar ao cadastro",
    
    // Landing page
    aiPoweredPlanning: "Planejamento de Peptídeos com IA",
    designYourPerfect: "Projete Seu Ciclo de",
    peptideCycle: "Peptídeos Perfeito",
    heroDescription: "Insira seu objetivo e deixe a IA criar um protocolo personalizado de peptídeos com dosagens precisas, timing, estimativas de custo e cronograma semanal.",
    selectGoals: "Selecione seus objetivos (escolha um ou mais)",
    additionalGoal: "Objetivo adicional (opcional)",
    additionalGoalPlaceholder: "ex: Melhorar qualidade da pele, função cognitiva...",
    bodyWeight: "Peso Corporal (kg)",
    experienceLevel: "Nível de Experiência",
    beginner: "Iniciante",
    intermediate: "Intermediário",
    advanced: "Avançado",
    generatingCycle: "Gerando Seu Ciclo...",
    generateMyCycle: "Gerar Meu Ciclo de Peptídeos",
    
    // Goals
    fatLoss: "Perda de Gordura / Peso",
    muscleBuilding: "Construção Muscular / Bulking",
    fatBurnLeanMuscle: "Queima de Gordura + Aumento de Massa Magra",
    enduranceRecovery: "Melhorar Resistência + Recuperação",
    antiAging: "Anti-Envelhecimento / Longevidade",
    recovery: "Recuperação / Cura",
    sleep: "Otimização do Sono",
    healing: "Cura de Lesões",
    popular: "Popular",
    
    // Features section
    everythingYouNeed: "Tudo que Você Precisa para Planejar Seu Ciclo",
    featuresDescription: "Nossa IA analisa seus objetivos e fornece protocolos completos de peptídeos com todos os detalhes necessários.",
    aiPoweredAnalysis: "Análise com IA",
    aiAnalysisDesc: "Recomendações GPT personalizadas para seus objetivos e nível de experiência",
    preciseDosing: "Dosagem Precisa",
    preciseDosingDesc: "Dosagens exatas em mg e UI com protocolos de timing para resultados ideais",
    cycleCalendar: "Calendário do Ciclo",
    cycleCalendarDesc: "Cronograma visual semanal com visualização exportável",
    costCalculator: "Calculadora de Custos",
    costCalculatorDesc: "Saiba exatamente quantos frascos você precisa e o investimento total",
    
    // How it works
    howItWorks: "Como Funciona",
    fourSimpleSteps: "Quatro passos simples para seu protocolo personalizado",
    step1Title: "Selecione Seus Objetivos",
    step1Desc: "Escolha um ou mais objetivos",
    step2Title: "Obtenha Análise da IA",
    step2Desc: "Receba recomendações personalizadas",
    step3Title: "Veja Seu Ciclo",
    step3Desc: "Protocolo completo com cronograma",
    step4Title: "Comece Sua Jornada",
    step4Desc: "Compre e comece",
    
    // Trust section
    researchBased: "Protocolos Baseados em Pesquisa",
    researchBasedDesc: "Nossas recomendações de IA são baseadas em pesquisas publicadas e protocolos estabelecidos. Sempre consulte um profissional de saúde antes de iniciar qualquer regime de peptídeos.",
    evidenceBased: "Baseado em Evidências",
    goalSpecific: "Específico por Objetivo",
    exportablePlans: "Planos Exportáveis",
    
    // Results page
    yourPersonalized: "Seu Protocolo de",
    cycleProtocol: "Ciclo Personalizado",
    goal: "Objetivo",
    duration: "Duração",
    weeks: "semanas",
    totalCost: "Custo Total",
    peptideProtocol: "Protocolo de Peptídeos",
    perDay: "por dia",
    timing: "Horário",
    vials: "frascos necessários",
    weeklySchedule: "Cronograma Semanal",
    week: "Semana",
    beginnerGuide: "Guia para Iniciantes",
    mixingInstructions: "Instruções de Mistura",
    storageGuidelines: "Diretrizes de Armazenamento",
    syringeRecommendations: "Recomendações de Seringas",
    purchaseLinks: "Onde Comprar",
    discountMessage: "Use estes links para descontos exclusivos em peptídeos de qualidade",
    saveCycle: "Salvar Este Ciclo",
    cycleSaved: "Ciclo salvo no seu painel!",
    exportPDF: "Exportar PDF",
    generateNew: "Gerar Novo Ciclo",
    
    // Results page additional
    aiRecommendation: "Resumo da Recomendação de IA",
    suppliesNeeded: "Materiais Necessários",
    mixingGuide: "Guia de Mistura Passo a Passo (Reconstituição)",
    mixingGuideDesc: "Siga estes passos cuidadosamente ao misturar seus peptídeos com água bacteriostática",
    injectionTips: "Dicas de Injeção",
    important: "Importante!",
    neverShake: "NUNCA agite ou gire o frasco vigorosamente - isso pode danificar as moléculas do peptídeo. Sempre deixe a água escorrer pela lateral do frasco e role suavemente entre as palmas das mãos.",
    cycleStart: "Início do Ciclo",
    cycleEnd: "Fim do Ciclo",
    scheduleFor: "Agenda para",
    noInjections: "Nenhuma injeção agendada para este dia",
    weeklyOverview: "Visão Geral do Protocolo Semanal",
    restDay: "Dia de descanso",
    exclusiveDiscount: "Desconto Exclusivo Disponível!",
    useAffiliateLinks: "Use nossos links de afiliados abaixo para obter descontos especiais em seus pedidos de peptídeos. Estes fornecedores confiáveis oferecem peptídeos de pesquisa de qualidade a preços competitivos.",
    shopAt: "Comprar em",
    getDiscount: "Obter Desconto",
    shoppingList: "Lista de Compras",
    pricePerVial: "Preço/Frasco",
    subtotal: "Subtotal",
    totalEstimatedCost: "Custo Total Estimado",
    pricesEstimate: "Os preços são estimativas. Use nossos links de afiliados acima para obter preços e descontos atuais.",
    tipsForSuccess: "Dicas para o Sucesso",
    importantWarnings: "Avisos Importantes",
    disclaimer: "Aviso Legal",
    disclaimerText: "Esta informação é apenas para fins de pesquisa e educacionais. Os peptídeos são vendidos estritamente para fins de pesquisa. Sempre consulte um profissional de saúde qualificado antes de iniciar qualquer novo suplemento ou composto de pesquisa. Os resultados individuais podem variar.",
    benefits: "Benefícios",
    sideEffects: "Efeitos Colaterais Potenciais",
    
    // Dashboard
    dashboard: "Painel",
    welcomeBack: "Bem-vindo de volta",
    savedCycles: "Ciclos Salvos",
    injectionReminders: "Lembretes de Injeção",
    affiliateAnalytics: "Análise de Afiliados",
    noCycles: "Nenhum ciclo salvo ainda",
    noReminders: "Nenhum lembrete definido",
    addReminder: "Adicionar Lembrete",
    peptideName: "Nome do Peptídeo",
    dosage: "Dosagem",
    time: "Horário",
    days: "Dias",
    save: "Salvar",
    cancel: "Cancelar",
    edit: "Editar",
    delete: "Excluir",
    totalClicks: "Total de Cliques",
    clicksByVendor: "Cliques por Fornecedor",
    
    // Navigation
    logout: "Sair",
    loggedOut: "Desconectado",
    
    // Errors and messages
    pleaseSelectGoal: "Por favor, selecione pelo menos um objetivo",
    cycleGenerated: "Ciclo gerado com sucesso!",
    failedToGenerate: "Falha ao gerar ciclo. Tente novamente.",
    verificationSent: "Código de verificação enviado! Verifique seu email.",
    emailVerified: "Email verificado! Bem-vindo ao PeptidePlanner AI",
    invalidCode: "Código de verificação inválido",
    newCodeSent: "Novo código de verificação enviado!",
    
    // Footer
    forResearch: "Apenas para fins de pesquisa e educacionais.",
  },
  
  es: {
    // Language selector
    selectLanguage: "Seleccione Su Idioma",
    continue: "Continuar",
    
    // Lead capture
    getStartedFree: "Comience Gratis",
    createAccount: "Cree su cuenta para acceder a recomendaciones de péptidos con IA",
    continueWithGoogle: "Continuar con Google",
    orUseEmail: "o use email",
    fullName: "Nombre Completo",
    emailAddress: "Correo Electrónico",
    phoneNumber: "Número de Teléfono",
    getAccess: "Obtener Acceso",
    secure: "Seguro",
    freeToUse: "Gratis para usar",
    verifyEmail: "Verifique Su Email",
    enterCode: "Ingrese el código de 6 dígitos enviado a",
    verificationCode: "Código de Verificación",
    demoMode: "Modo Demo - Su código es:",
    verifyAndContinue: "Verificar y Continuar",
    resendCode: "Reenviar código de verificación",
    backToSignUp: "← Volver al registro",
    
    // Landing page
    aiPoweredPlanning: "Planificación de Péptidos con IA",
    designYourPerfect: "Diseñe Su Ciclo de",
    peptideCycle: "Péptidos Perfecto",
    heroDescription: "Ingrese su objetivo y deje que la IA cree un protocolo personalizado de péptidos con dosis precisas, timing, estimaciones de costo y horario semanal.",
    selectGoals: "Seleccione sus objetivos (elija uno o más)",
    additionalGoal: "Objetivo adicional (opcional)",
    additionalGoalPlaceholder: "ej: Mejorar calidad de piel, función cognitiva...",
    bodyWeight: "Peso Corporal (kg)",
    experienceLevel: "Nivel de Experiencia",
    beginner: "Principiante",
    intermediate: "Intermedio",
    advanced: "Avanzado",
    generatingCycle: "Generando Su Ciclo...",
    generateMyCycle: "Generar Mi Ciclo de Péptidos",
    
    // Goals
    fatLoss: "Pérdida de Grasa / Peso",
    muscleBuilding: "Construcción Muscular / Bulking",
    fatBurnLeanMuscle: "Quema de Grasa + Aumento de Masa Magra",
    enduranceRecovery: "Mejorar Resistencia + Recuperación",
    antiAging: "Anti-Envejecimiento / Longevidad",
    recovery: "Recuperación / Curación",
    sleep: "Optimización del Sueño",
    healing: "Curación de Lesiones",
    popular: "Popular",
    
    // Features section
    everythingYouNeed: "Todo lo que Necesita para Planificar Su Ciclo",
    featuresDescription: "Nuestra IA analiza sus objetivos y proporciona protocolos completos de péptidos con todos los detalles necesarios.",
    aiPoweredAnalysis: "Análisis con IA",
    aiAnalysisDesc: "Recomendaciones GPT personalizadas para sus objetivos y nivel de experiencia",
    preciseDosing: "Dosificación Precisa",
    preciseDosingDesc: "Dosis exactas en mg y UI con protocolos de timing para resultados óptimos",
    cycleCalendar: "Calendario del Ciclo",
    cycleCalendarDesc: "Horario visual semanal con vista exportable",
    costCalculator: "Calculadora de Costos",
    costCalculatorDesc: "Sepa exactamente cuántos viales necesita y la inversión total requerida",
    
    // How it works
    howItWorks: "Cómo Funciona",
    fourSimpleSteps: "Cuatro pasos simples para su protocolo personalizado",
    step1Title: "Seleccione Sus Objetivos",
    step1Desc: "Elija uno o más objetivos",
    step2Title: "Obtenga Análisis de IA",
    step2Desc: "Reciba recomendaciones personalizadas",
    step3Title: "Vea Su Ciclo",
    step3Desc: "Protocolo completo con horario",
    step4Title: "Comience Su Viaje",
    step4Desc: "Compre y comience",
    
    // Trust section
    researchBased: "Protocolos Basados en Investigación",
    researchBasedDesc: "Nuestras recomendaciones de IA se basan en investigaciones publicadas y protocolos establecidos. Siempre consulte con un profesional de salud antes de comenzar cualquier régimen de péptidos.",
    evidenceBased: "Basado en Evidencia",
    goalSpecific: "Específico por Objetivo",
    exportablePlans: "Planes Exportables",
    
    // Results page
    yourPersonalized: "Su Protocolo de",
    cycleProtocol: "Ciclo Personalizado",
    goal: "Objetivo",
    duration: "Duración",
    weeks: "semanas",
    totalCost: "Costo Total",
    peptideProtocol: "Protocolo de Péptidos",
    perDay: "por día",
    timing: "Horario",
    vials: "viales necesarios",
    weeklySchedule: "Horario Semanal",
    week: "Semana",
    beginnerGuide: "Guía para Principiantes",
    mixingInstructions: "Instrucciones de Mezcla",
    storageGuidelines: "Guías de Almacenamiento",
    syringeRecommendations: "Recomendaciones de Jeringas",
    purchaseLinks: "Dónde Comprar",
    discountMessage: "Use estos enlaces para descuentos exclusivos en péptidos de calidad",
    saveCycle: "Guardar Este Ciclo",
    cycleSaved: "¡Ciclo guardado en su panel!",
    exportPDF: "Exportar PDF",
    generateNew: "Generar Nuevo Ciclo",
    
    // Results page additional
    aiRecommendation: "Resumen de Recomendación de IA",
    suppliesNeeded: "Materiales Necesarios",
    mixingGuide: "Guía de Mezcla Paso a Paso (Reconstitución)",
    mixingGuideDesc: "Siga estos pasos cuidadosamente al mezclar sus péptidos con agua bacteriostática",
    injectionTips: "Consejos de Inyección",
    important: "¡Importante!",
    neverShake: "NUNCA agite ni gire el vial vigorosamente - esto puede dañar las moléculas del péptido. Siempre deje que el agua gotee por el lado del vial y ruede suavemente entre las palmas de sus manos.",
    cycleStart: "Inicio del Ciclo",
    cycleEnd: "Fin del Ciclo",
    scheduleFor: "Horario para",
    noInjections: "No hay inyecciones programadas para este día",
    weeklyOverview: "Visión General del Protocolo Semanal",
    restDay: "Día de descanso",
    exclusiveDiscount: "¡Descuento Exclusivo Disponible!",
    useAffiliateLinks: "Use nuestros enlaces de afiliados a continuación para obtener descuentos especiales en sus pedidos de péptidos. Estos proveedores de confianza ofrecen péptidos de investigación de calidad a precios competitivos.",
    shopAt: "Comprar en",
    getDiscount: "Obtener Descuento",
    shoppingList: "Lista de Compras",
    pricePerVial: "Precio/Vial",
    subtotal: "Subtotal",
    totalEstimatedCost: "Costo Total Estimado",
    pricesEstimate: "Los precios son estimaciones. Use nuestros enlaces de afiliados arriba para obtener precios y descuentos actuales.",
    tipsForSuccess: "Consejos para el Éxito",
    importantWarnings: "Advertencias Importantes",
    disclaimer: "Aviso Legal",
    disclaimerText: "Esta información es solo para fines de investigación y educativos. Los péptidos se venden estrictamente para fines de investigación. Siempre consulte con un profesional de salud calificado antes de comenzar cualquier nuevo suplemento o compuesto de investigación. Los resultados individuales pueden variar.",
    benefits: "Beneficios",
    sideEffects: "Efectos Secundarios Potenciales",
    
    // Dashboard
    dashboard: "Panel",
    welcomeBack: "Bienvenido de vuelta",
    savedCycles: "Ciclos Guardados",
    injectionReminders: "Recordatorios de Inyección",
    affiliateAnalytics: "Análisis de Afiliados",
    noCycles: "No hay ciclos guardados aún",
    noReminders: "No hay recordatorios configurados",
    addReminder: "Agregar Recordatorio",
    peptideName: "Nombre del Péptido",
    dosage: "Dosis",
    time: "Hora",
    days: "Días",
    save: "Guardar",
    cancel: "Cancelar",
    edit: "Editar",
    delete: "Eliminar",
    totalClicks: "Total de Clics",
    clicksByVendor: "Clics por Proveedor",
    
    // Navigation
    logout: "Cerrar Sesión",
    loggedOut: "Sesión cerrada",
    
    // Errors and messages
    pleaseSelectGoal: "Por favor, seleccione al menos un objetivo",
    cycleGenerated: "¡Ciclo generado con éxito!",
    failedToGenerate: "Error al generar ciclo. Intente de nuevo.",
    verificationSent: "¡Código de verificación enviado! Revise su email.",
    emailVerified: "¡Email verificado! Bienvenido a PeptidePlanner AI",
    invalidCode: "Código de verificación inválido",
    newCodeSent: "¡Nuevo código de verificación enviado!",
    
    // Footer
    forResearch: "Solo para fines de investigación y educativos.",
  }
};

export function LanguageProvider({ children }) {
  const [language, setLanguage] = useState(() => {
    // First check localStorage for saved preference
    const savedLang = localStorage.getItem('preferredLanguage');
    if (savedLang) return savedLang;
    
    // Auto-detect from browser settings
    return detectBrowserLanguage();
  });

  useEffect(() => {
    if (language) {
      localStorage.setItem('preferredLanguage', language);
    }
  }, [language]);

  const t = (key) => {
    if (!language) return translations.en[key] || key;
    return translations[language]?.[key] || translations.en[key] || key;
  };

  const value = {
    language,
    setLanguage,
    t,
    isLanguageSelected: true // Always true now since we auto-detect
  };

  return (
    <LanguageContext.Provider value={value}>
      {children}
    </LanguageContext.Provider>
  );
}

export function useLanguage() {
  const context = useContext(LanguageContext);
  if (!context) {
    throw new Error('useLanguage must be used within a LanguageProvider');
  }
  return context;
}
